import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, Cargando, ErrorApi, formatFecha } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { exportarCSV, exportarPDF, type ColumnaTabla } from '../shared/exportar.ts';
import type { Solicitud } from '../shared/tipos.ts';

type PersonaAsignada = {
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  estado: string;
};

type Fila = Solicitud & {
  salon_nombre: string;
  convocatorias_total: number;
  convocatorias_aceptadas: number;
  convocatorias_pendientes: number;
  convocatorias_rechazadas: number;
  personas: PersonaAsignada[];
};

function estadoBadge(estado: string, aceptadas: number, total: number): { texto: string; clase: string } {
  if (estado === 'cancelada') return { texto: 'Cancelada', clase: 'badge-error' };
  if (estado === 'finalizada') {
    if (aceptadas >= total && total > 0) return { texto: 'Completa', clase: 'badge-ok' };
    return { texto: 'Finalizada', clase: 'badge-neutral' };
  }
  return { texto: estado, clase: 'badge-neutral' };
}

function personaBadge(estado: string): { texto: string; clase: string } {
  switch (estado) {
    case 'aceptada': return { texto: 'Confirmado', clase: 'badge-ok' };
    case 'enviada': return { texto: 'Pendiente', clase: 'badge-pendiente' };
    case 'rechazada': return { texto: 'Rechazado', clase: 'badge-error' };
    default: return { texto: estado, clase: 'badge-neutral' };
  }
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function getMesKey(fechaStr: string): string {
  return fechaStr.slice(0, 7); // "2026-07"
}

function getMesLabel(key: string): string {
  const [anio, m] = key.split('-');
  return `${MESES[parseInt(m, 10) - 1]} ${anio}`;
}

function Icono({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const columnas: ColumnaTabla[] = [
  { clave: 'fecha', etiqueta: 'Fecha', formato: 'fecha' },
  { clave: 'salon_nombre', etiqueta: 'Salón' },
  { clave: 'hora_inicio', etiqueta: 'Inicio' },
  { clave: 'hora_fin', etiqueta: 'Fin' },
  { clave: 'estado', etiqueta: 'Estado' },
  { clave: 'convocatorias_aceptadas', etiqueta: 'Confirmados', formato: 'numero' },
  { clave: 'convocatorias_total', etiqueta: 'Total pedidos', formato: 'numero' },
  { clave: 'personas_str', etiqueta: 'Personas' },
];

export function HistorialPage() {
  const { datos, cargando, error, recargar } = useApi<Fila[]>('/api/solicitudes');
  const navigate = useNavigate();

  const [filtroSalon, setFiltroSalon] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [expandida] = useState<string | null>(null);

  const historial = useMemo(() => {
    if (!datos) return [];
    return datos
      .filter(s => ['finalizada', 'cancelada'].includes(s.estado))
      .filter(s => !filtroSalon || s.salon_nombre === filtroSalon)
      .filter(s => !filtroEstado || s.estado === filtroEstado)
      .filter(s => !filtroDesde || s.fecha >= filtroDesde)
      .filter(s => !filtroHasta || s.fecha <= filtroHasta);
  }, [datos, filtroSalon, filtroEstado, filtroDesde, filtroHasta]);

  const porMes = useMemo(() => {
    const mapa = new Map<string, Fila[]>();
    // meses más recientes primero
    const ordenado = [...historial].sort((a, b) => b.fecha.localeCompare(a.fecha));
    for (const s of ordenado) {
      const key = getMesKey(s.fecha);
      const lista = mapa.get(key) || [];
      lista.push(s);
      mapa.set(key, lista);
    }
    return mapa;
  }, [historial]);
  const [mesAbierto, setMesAbierto] = useState<string | null>(null);

  const salonesUnicos = useMemo(() => {
    if (!datos) return [];
    return [...new Set(datos.filter(s => ['finalizada', 'cancelada'].includes(s.estado)).map(s => s.salon_nombre))].sort();
  }, [datos]);

  function exportar(tipo: 'csv' | 'pdf') {
    const filas = historial.map(s => ({
      ...s,
      personas_str: s.personas.map(p => `${p.persona_apellido} ${p.persona_nombre} (${p.estado})`).join(', '),
    }));
    if (tipo === 'csv') exportarCSV(filas, columnas, 'historial-solicitudes.csv');
    else exportarPDF(filas, columnas, 'Historial de solicitudes', 'historial-solicitudes.pdf');
  }

  const hayFiltros = filtroSalon || filtroEstado || filtroDesde || filtroHasta;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutro-800">Historial de solicitudes</h2>
          <p className="text-sm text-neutro-500 mt-0.5">{historial.length} solicitudes {hayFiltros ? 'filtradas' : 'en total'}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl border border-contorno-var bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[140px]">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Salón</span>
            <select className="campo" value={filtroSalon} onChange={(e) => setFiltroSalon(e.target.value)}>
              <option value="">Todos</option>
              {salonesUnicos.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="flex-1 min-w-[140px]">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Estado</span>
            <select className="campo" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="">Todos</option>
              <option value="finalizada">Finalizada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </label>
          <label className="flex-1 min-w-[140px]">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Desde</span>
            <input className="campo" type="date" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} />
          </label>
          <label className="flex-1 min-w-[140px]">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-neutro-500">Hasta</span>
            <input className="campo" type="date" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} />
          </label>
          <div className="flex gap-2">
            {historial.length > 0 && (
              <>
                <button onClick={() => exportar('csv')} className="boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 text-xs">
                  <Icono d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" className="h-3.5 w-3.5" />
                  CSV
                </button>
                <button onClick={() => exportar('pdf')} className="boton border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 text-xs">
                  <Icono d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m.75 12 3 3m0 0 3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" className="h-3.5 w-3.5" />
                  PDF
                </button>
              </>
            )}
          </div>
        </div>
        {hayFiltros && (
          <button
            onClick={() => { setFiltroSalon(''); setFiltroEstado(''); setFiltroDesde(''); setFiltroHasta(''); }}
            className="mt-3 text-xs font-medium text-primario-400 hover:text-primario-500 transition-colors"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Content */}
      {cargando ? (
        <Cargando />
      ) : error ? (
        <ErrorApi mensaje={error} onReintentar={recargar} />
      ) : historial.length > 0 ? (
        Array.from(porMes.entries()).map(([mes, solicitudes]) => (
          <section key={mes}>
            <button
              className="mb-3 flex w-full items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm border border-contorno-var text-left"
              onClick={() => setMesAbierto(mesAbierto === mes ? null : mes)}
            >
              <div>
                <h3 className="text-base font-bold text-neutro-800">{getMesLabel(mes)}</h3>
                <p className="text-xs text-neutro-400">{solicitudes.length} servicio{solicitudes.length > 1 ? 's' : ''}</p>
              </div>
              <span className="text-lg font-bold text-primario-400">{mesAbierto === mes ? '▾' : '▸'}</span>
            </button>
            {mesAbierto === mes && (
            <ul className="space-y-2">
              {solicitudes.map((s) => {
                const badge = estadoBadge(s.estado, s.convocatorias_aceptadas, s.convocatorias_total);
                const expandido = expandida === s.id;
                const accentColor = s.estado === 'cancelada' ? 'border-l-red-400' : badge.clase === 'badge-ok' ? 'border-l-green-400' : 'border-l-primario-400';
                return (
                  <li key={s.id} className={`tarjeta !p-0 overflow-hidden border-l-4 ${accentColor}`}>
                    <button onClick={() => navigate(`/cliente/solicitud/${s.id}`)} className="w-full p-4 text-left">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-base font-semibold text-neutro-800">{formatFecha(s.fecha)}</p>
                            <span className="text-neutro-300">·</span>
                            <p className="text-sm text-neutro-500">{formatoRango(s.hora_inicio, s.hora_fin)}</p>
                          </div>
                          <p className="text-sm font-medium text-neutro-600">{s.salon_nombre}</p>
                          <p className="mt-1 text-xs text-neutro-400">
                            {s.convocatorias_aceptadas}/{s.convocatorias_total} confirmados
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`badge ${badge.clase}`}>{badge.texto}</span>
                          {/* Avatars stacked */}
                          {s.convocatorias_aceptadas > 0 && (
                            <div className="flex -space-x-2">
                              {s.personas.filter(p => p.estado === 'aceptada').slice(0, 3).map((p, i) => (
                                <div key={i} className="flex h-7 w-7 items-center justify-center rounded-full bg-primario-100 border-2 border-white text-[10px] font-bold text-primario-500">
                                  {p.persona_apellido.charAt(0)}{p.persona_nombre.charAt(0)}
                                </div>
                              ))}
                              {s.convocatorias_aceptadas > 3 && (
                                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutro-100 border-2 border-white text-[10px] font-bold text-neutro-500">
                                  +{s.convocatorias_aceptadas - 3}
                                </div>
                              )}
                            </div>
                          )}
                          <Icono d="m19.5 8.25-7.5 7.5-7.5-7.5" className="h-4 w-4 text-neutro-400 transition-transform" />
                        </div>
                      </div>
                    </button>
                    {/* Expanded detail */}
                    {expandido && (
                      <div className="border-t border-contorno-var bg-superficie px-4 pb-4 pt-3">
                        {s.personas.length > 0 ? (
                          <>
                            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-neutro-400">Personas asignadas</p>
                            <ul className="space-y-1.5">
                              {s.personas.map((p, i) => {
                                const pb = personaBadge(p.estado);
                                return (
                                  <li key={i} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm border border-contorno-var">
                                    <div className="flex items-center gap-2">
                                      <div className={`h-1.5 w-1.5 rounded-full ${p.estado === 'aceptada' ? 'bg-estado-ok' : p.estado === 'rechazada' ? 'bg-estado-error' : 'bg-estado-pendiente'}`} />
                                      <span className="text-xs font-medium text-neutro-400 uppercase">{p.categoria_nombre}</span>
                                      <span className="text-neutro-700">{p.persona_apellido} {p.persona_nombre}</span>
                                    </div>
                                    <span className={`badge ${pb.clase}`}>{pb.texto}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        ) : (
                          <p className="text-sm text-neutro-400 italic">Sin personas asignadas</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            )}
          </section>
        ))
      ) : (
        <div className="tarjeta text-center py-12">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-superficie-200">
            <Icono d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" className="h-7 w-7 text-neutro-400" />
          </div>
          <p className="text-sm font-medium text-neutro-600">
            {hayFiltros ? 'No hay solicitudes que coincidan con los filtros.' : 'No hay solicitudes en el historial.'}
          </p>
        </div>
      )}
    </div>
  );
}
